/**
 * Connection state. navigator.onLine only says a network interface is up (a phone on site Wi-Fi with no
 * backhaul still reports online), so real request outcomes and a health probe decide whether the server is
 * reachable.
 */
type Listener = () => void;

export type ConnectionState = 'online' | 'offline';

const listeners = new Set<Listener>();
let reachable = true;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probeDelay = 4000;
const MAX_PROBE_DELAY = 30000;

const browserOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

export function connectionState(): ConnectionState {
  return browserOnline() && reachable ? 'online' : 'offline';
}

export const isOnline = () => connectionState() === 'online';

function emit() { listeners.forEach((listener) => listener()); }

export function subscribeConnection(listener: Listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function scheduleProbe() {
  if (probeTimer || typeof window === 'undefined') return;
  probeTimer = setTimeout(async () => {
    probeTimer = null;
    if (!browserOnline()) { scheduleProbe(); return; }
    try {
      const response = await fetch('/api/health', { cache: 'no-store', credentials: 'same-origin' });
      if (response.ok) { markReachable(); return; }
    } catch { /* still unreachable */ }
    probeDelay = Math.min(MAX_PROBE_DELAY, probeDelay * 1.6);
    scheduleProbe();
  }, probeDelay);
}

export function markReachable() {
  probeDelay = 4000;
  if (probeTimer) { clearTimeout(probeTimer); probeTimer = null; }
  if (reachable) return;
  reachable = true;
  emit();
}

export function markUnreachable() {
  scheduleProbe();
  if (!reachable) return;
  reachable = false;
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { probeDelay = 1000; markUnreachable(); scheduleProbe(); emit(); });
  window.addEventListener('offline', emit);
}
